import React from 'react'
import { Button } from 'antd'
import {
  CheckCircleFilled,
  CloseOutlined,
  CopyOutlined,
  ExclamationCircleFilled,
  InfoCircleOutlined,
  StarFilled
} from '@ant-design/icons'
import wanganqiangQrCode from '../../../images/service-qrcode/wanganqiang_qr_image.png'
import './ClawPaymentSuccessModal.scss'

export interface PaymentSuccessInfo {
  orderId: string
  planName: string
  amount: number
  /** webhook 通知是否失败（3次重试后仍失败） */
  webhookFailed?: boolean
  /** webhook 失败时展示的详细信息，方便用户截图发给顾问 */
  failDetail?: {
    teamId: string
    groupId: string
    teamName: string
    userId: string
    contact: string
    phone: string
  }
}

interface Props {
  visible: boolean
  info: PaymentSuccessInfo
  onClose: () => void
  onTrack?: (actionType: any) => void
}

const ClawPaymentSuccessModal: React.FC<Props> = ({
  visible,
  info,
  onClose,
  onTrack
}) => {
  if (!visible) return null

  const isFailed = info.webhookFailed === true

  return (
    <div className="claw-success-modal" onClick={onClose}>
      <div
        className="claw-success-modal__card"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="claw-success-modal__close" onClick={onClose}>
          <CloseOutlined />
        </button>

        <div className="claw-success-modal__body">
          {/* 状态图标 */}
          <div
            className={`claw-success-modal__icon${
              isFailed ? ' claw-success-modal__icon--warn' : ''
            }`}
          >
            {isFailed ? <ExclamationCircleFilled /> : <CheckCircleFilled />}
          </div>

          {/* 主标题 */}
          <h2 className="claw-success-modal__title">
            {isFailed ? '订单处理异常' : '支付成功！'}
          </h2>

          {/* 副标题 */}
          <p className="claw-success-modal__subtitle">
            {isFailed
              ? '支付已完成，但订单未能自动处理，请联系专属顾问手动开通'
              : '请扫码添加专属顾问，协助开通您的数字员工'}
          </p>

          {/* 订单信息卡片 */}
          <div className="claw-success-modal__order">
            <h3 className="claw-success-modal__order-header">订单信息</h3>
            <div className="claw-success-modal__order-row">
              <span className="claw-success-modal__order-label">订单编号</span>
              <span className="claw-success-modal__order-value claw-success-modal__order-value--id">
                {info.orderId}
              </span>
            </div>
            <div className="claw-success-modal__order-row">
              <span className="claw-success-modal__order-label">套餐名称</span>
              <span className="claw-success-modal__order-value">
                {info.planName}
              </span>
            </div>
            <div className="claw-success-modal__order-row">
              <span className="claw-success-modal__order-label">支付金额</span>
              <span className="claw-success-modal__order-value claw-success-modal__order-value--amount">
                ¥{info.amount}（激活费 · 正式版全额抵扣）
              </span>
            </div>
            <div className="claw-success-modal__order-row">
              <span className="claw-success-modal__order-label">订单类型</span>
              <span className="claw-success-modal__order-value">
                数字员工激活费
              </span>
            </div>
            <p className="claw-success-modal__order-tip">
              <InfoCircleOutlined /> 激活费将在正式版全额抵扣，不满意可随时退款
            </p>
          </div>

          {/* 失败时展示完整订单详情，方便截图 */}
          {isFailed && info.failDetail && (
            <div className="claw-success-modal__fail-detail">
              <h3 className="claw-success-modal__fail-detail-header">
                <CopyOutlined /> 请截图保存以下信息，发送给专属顾问
              </h3>
              <div className="claw-success-modal__fail-detail-grid">
                <span className="claw-success-modal__fail-label">订单编号</span>
                <span className="claw-success-modal__fail-value">
                  {info.orderId}
                </span>
                <span className="claw-success-modal__fail-label">套餐名称</span>
                <span className="claw-success-modal__fail-value">
                  {info.planName}
                </span>
                <span className="claw-success-modal__fail-label">支付金额</span>
                <span className="claw-success-modal__fail-value">
                  ¥{info.amount}
                </span>
                <span className="claw-success-modal__fail-label">团队名称</span>
                <span className="claw-success-modal__fail-value">
                  {info.failDetail.teamName}
                </span>
                <span className="claw-success-modal__fail-label">团队ID</span>
                <span className="claw-success-modal__fail-value">
                  {info.failDetail.teamId}
                </span>
                <span className="claw-success-modal__fail-label">联系人</span>
                <span className="claw-success-modal__fail-value">
                  {info.failDetail.contact}
                </span>
                <span className="claw-success-modal__fail-label">手机号</span>
                <span className="claw-success-modal__fail-value">
                  {info.failDetail.phone}
                </span>
                <span className="claw-success-modal__fail-label">用户ID</span>
                <span className="claw-success-modal__fail-value">
                  {info.failDetail.userId}
                </span>
                <span className="claw-success-modal__fail-label">GroupID</span>
                <span className="claw-success-modal__fail-value">
                  {info.failDetail.groupId}
                </span>
              </div>
            </div>
          )}

          {/* 专属顾问二维码 */}
          <div
            className="claw-success-modal__advisor-full"
            onClick={() => onTrack?.('contactUs')}
          >
            <h2 className="claw-success-modal__advisor-full-title">
              <StarFilled />{' '}
              {isFailed ? '扫码联系顾问处理订单' : '扫码联系专属顾问'}
            </h2>
            <p className="claw-success-modal__advisor-full-desc">
              {isFailed
                ? '请将订单编号发送给顾问，我们将尽快为您处理'
                : '协助您开通内测权限，配置专属数字员工'}
            </p>
            <div className="claw-success-modal__advisor-full-qr">
              <img src={wanganqiangQrCode} alt="资深产品专家" />
            </div>
          </div>

          {/* 关闭按钮 */}
          <Button
            className="claw-success-modal__btn"
            type="primary"
            size="large"
            onClick={onClose}
          >
            我已添加专属顾问
          </Button>
        </div>
      </div>
    </div>
  )
}

export default ClawPaymentSuccessModal
